import { useState } from 'react';
import DatePicker from '../DatePicker';
import { parseDate, calculateDuration } from '../../utils/dateUtils';
import { useTranslation } from '../../i18n';
import { SIDEBAR_MODE, DATE_PICKER_FIELD } from '../../constants/types';
import { TitleField, DescriptionField, ProjectField, FormActions } from './shared/FormFields';
import useFormState from './shared/useFormState';
import './EventForm.scss';

/**
 * Event form component for creating and editing events.
 * Handles event-specific fields: start date, end date, duration, and date locks.
 * 
 * @param {Object} props
 * @param {string} props.mode - 'create' or 'edit'
 * @param {Object} props.data - Event data for edit mode
 * @param {Array} props.projects - Available projects
 * @param {string} props.selectedProjectId - Pre-selected project for new items
 * @param {Function} props.onSave - Save handler (formData) => void
 * @param {Function} props.onDelete - Delete handler (id) => void
 * @param {Function} props.onClose - Close handler
 * @param {Function} props.onChange - Live preview change handler
 */
function EventForm({ 
  mode, 
  data, 
  projects,
  selectedProjectId,
  onSave, 
  onDelete, 
  onClose,
  onChange
}) {
  const { t, formatDate } = useTranslation();
  
  // Use shared form state for common fields
  const { 
    title, 
    description, 
    projectId, 
    handleTitleChange, 
    handleDescriptionChange, 
    handleProjectChange,
    notifyChange,
    inputRef 
  } = useFormState({ mode, data, selectedProjectId, projects, onChange });

  // Event-specific date state
  const initialStartDate = data?.startDate ? parseDate(data.startDate) : new Date();
  const initialEndDate = data?.endDate ? parseDate(data.endDate) : new Date();

  const [startDate, setStartDate] = useState(initialStartDate);
  const [endDate, setEndDate] = useState(initialEndDate);
  
  // Lock state
  const [lockStart, setLockStart] = useState(Boolean(data?.lockStart));
  const [lockEnd, setLockEnd] = useState(Boolean(data?.lockEnd));
  const [lockDuration, setLockDuration] = useState(Boolean(data?.lockDuration));
  
  // Date picker visibility
  const [activePicker, setActivePicker] = useState(null);

  const handleStartDateChange = (newDate) => {
    if (lockStart) return;

    if (lockDuration && !lockEnd) {
      // If duration is locked and end is free, move both dates
      const durationMs = Math.max(0, endDate.getTime() - startDate.getTime());
      const newEndDate = new Date(newDate.getTime() + durationMs);
      setStartDate(newDate);
      setEndDate(newEndDate);
      notifyChange({ startDate: newDate, endDate: newEndDate });
      return;
    }

    // Normal behavior: just update start date
    setStartDate(newDate);
    if (newDate > endDate) {
      // If new start is after end, move end to match
      setEndDate(newDate);
      notifyChange({ startDate: newDate, endDate: newDate });
    } else {
      notifyChange({ startDate: newDate });
    }
  };

  const handleEndDateChange = (newDate) => {
    if (lockEnd) return;

    if (lockDuration && !lockStart) {
      // If duration is locked and start is free, move both dates
      const durationMs = Math.max(0, endDate.getTime() - startDate.getTime());
      const newStartDate = new Date(newDate.getTime() - durationMs);
      setEndDate(newDate);
      setStartDate(newStartDate);
      notifyChange({ startDate: newStartDate, endDate: newDate });
      return;
    }

    // Normal behavior: just update end date
    setEndDate(newDate);
    if (newDate < startDate) {
      // If new end is before start, move start to match
      setStartDate(newDate);
      notifyChange({ startDate: newDate, endDate: newDate });
    } else {
      notifyChange({ endDate: newDate });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    if (!projectId) return;

    onSave({
      title: title.trim(),
      description: description.trim(),
      projectId,
      startDate,
      endDate,
      lockStart,
      lockEnd,
      lockDuration
    });
  };

  const handleDeleteClick = () => {
    onDelete(data.id);
  };

  const isEditing = mode === SIDEBAR_MODE.EDIT;
  const headerTitle = isEditing ? t('event.edit') : t('event.new');
  
  // Check if event is read-only (from shared project with read permission)
  const selectedProject = projects.find(p => p.id === projectId);
  const isReadOnly = selectedProject?.permission === 'read';
  const ownerInfo = isReadOnly && selectedProject?.sharedBy 
    ? `${t('sharing.sharedBy')} ${selectedProject.sharedBy}` 
    : null;
  
  // Format duration text
  const formatDurationText = (totalDays, workDays) => {
    if (totalDays === 1 && workDays === 1) {
      return t('event.duration', { days: totalDays, workdays: workDays });
    } else if (totalDays === 1) {
      return t('event.durationSingularWorkdays', { days: totalDays, workdays: workDays });
    } else if (workDays === 1) {
      return t('event.durationPluralWorkday', { days: totalDays, workdays: workDays });
    } else {
      return t('event.durationPlural', { days: totalDays, workdays: workDays });
    }
  };

  return (
    <>
      <div className="event-form__header">
        <h3 className="event-form__title">{headerTitle}</h3>
        <button 
          className="event-form__close" 
          onClick={onClose}
          title={t('common.close')}
        >
          {'\u00D7'}
        </button>
      </div>

      {/* Read-only notice for shared items */}
      {isReadOnly && (
        <div className="event-form__readonly-notice">
          <span className="event-form__readonly-icon">ℹ️</span>
          <div className="event-form__readonly-text">
            <div>{t('sharing.readOnlyInfo')}</div>
            {ownerInfo && <div className="event-form__readonly-owner">{ownerInfo}</div>}
          </div>
        </div>
      )}

      {/* Date fields */}
      <div className="event-form__dates-section">
        <div className="event-form__date-range">
          <div className="event-form__date-field">
            <div className="event-form__date-with-lock">
              <button
                type="button"
                className={`event-form__date-btn ${lockStart ? 'event-form__date-btn--locked' : ''}`}
                onClick={() => !lockStart && !isReadOnly && setActivePicker(activePicker === DATE_PICKER_FIELD.START ? null : DATE_PICKER_FIELD.START)}
                disabled={lockStart || isReadOnly}
              >
                {formatDate(startDate)}
              </button>
              {!isReadOnly && (
                <button
                  type="button"
                  className={`event-form__lock-btn ${lockStart ? 'event-form__lock-btn--locked' : ''}`}
                  onClick={() => setLockStart(!lockStart)}
                  title={lockStart ? t('event.unlockStart') : t('event.lockStart')}
                >
                  🔒
                </button>
              )}
            </div>
            {activePicker === DATE_PICKER_FIELD.START && !lockStart && (
              <DatePicker
                value={startDate}
                onChange={handleStartDateChange}
                onClose={() => setActivePicker(null)}
                maxDate={endDate}
              />
            )}
          </div>
          <span className="event-form__date-separator">{'\u2014'}</span>
          <div className="event-form__date-field">
            <div className="event-form__date-with-lock">
              <button
                type="button"
                className={`event-form__date-btn ${lockEnd ? 'event-form__date-btn--locked' : ''}`}
                onClick={() => !lockEnd && !isReadOnly && setActivePicker(activePicker === DATE_PICKER_FIELD.END ? null : DATE_PICKER_FIELD.END)}
                disabled={lockEnd || isReadOnly}
              >
                {formatDate(endDate)}
              </button>
              {!isReadOnly && (
                <button
                  type="button"
                  className={`event-form__lock-btn ${lockEnd ? 'event-form__lock-btn--locked' : ''}`}
                  onClick={() => setLockEnd(!lockEnd)}
                  title={lockEnd ? t('event.unlockEnd') : t('event.lockEnd')}
                >
                  🔒
                </button>
              )}
            </div>
            {activePicker === DATE_PICKER_FIELD.END && !lockEnd && (
              <DatePicker
                value={endDate}
                onChange={handleEndDateChange}
                onClose={() => setActivePicker(null)}
                minDate={startDate}
              />
            )}
          </div>
        </div>
        <div className="event-form__duration-row">
          <div className="event-form__duration">
            {(() => {
              const { totalDays, workDays } = calculateDuration(startDate, endDate);
              return formatDurationText(totalDays, workDays);
            })()}
          </div>
          <button
            type="button"
            className={`event-form__lock-btn event-form__lock-btn--duration ${lockDuration ? 'event-form__lock-btn--locked' : ''}`}
            onClick={() => setLockDuration(!lockDuration)}
            title={lockDuration ? t('event.unlockDuration') : t('event.lockDuration')}
          >
            🔒
          </button>
        </div>
      </div>

      <form className="event-form__form" onSubmit={handleSubmit}>
        <TitleField
          value={title}
          onChange={handleTitleChange}
          placeholder={t('event.placeholder')}
          inputRef={inputRef}
          disabled={isReadOnly}
        />

        <DescriptionField
          value={description}
          onChange={handleDescriptionChange}
          disabled={isReadOnly}
        />

        <ProjectField
          value={projectId}
          onChange={handleProjectChange}
          projects={projects}
          disabled={isReadOnly}
        />

        <FormActions
          mode={mode}
          entityType="event"
          entityTitle={data?.title}
          onDelete={handleDeleteClick}
          onClose={onClose}
          onSubmit={handleSubmit}
          canSubmit={title.trim() && projectId}
          disabled={isReadOnly}
        />
      </form>
    </>
  );
}

export default EventForm;
